import moment from "moment";
import React from 'react';
import {useAllLocations, usePM10FiltersByMiddleDate} from "../../sdk/queries";
import {Grid} from "../Grid";
import {useNavBar} from "../NavBar";
import {Card} from "../TabsRoute/components/Card";
import {ConcentrationPlot} from "./components/ConcentrationPlot";
import {DataListCard} from "./components/DataListCard";
import {MapCard} from "./components/MapCard";
import {ReportDownload} from "./components/ReportDownload";
import {dateFormat, recordsLimit} from "./constants";
import {AQMContext, Measurements} from "./context";


export const AirQualityMonitoring: React.FC = () => {

    const {updatePage} = useNavBar();
    React.useLayoutEffect(() => {
        // Let the navbar know the current page displayed is this one
        updatePage("air-quality-monitoring");
    }, []);

    const [currentLocationId, setCurrentLocationId] = React.useState(2);

    const {data: locationsData, loading: loadingLocations} = useAllLocations();

    const [targetDate, setTargetDate] = React.useState(() => {
        const now = moment();
        return {
            unix: now.unix(),
            formatted: now.format(dateFormat),
        }
    });


    const {data, loading: loadingFilters} = usePM10FiltersByMiddleDate({
        variables: {
            locationId: String(currentLocationId),
            middleDate: targetDate.formatted,
            limit: String(recordsLimit),
        },
    });

    const cleanedData = data?.data ?? [];

    const dateRange = React.useMemo(() => {
        if (cleanedData.length > 1) {
            const fromDate = moment(cleanedData[0].exposureOn, dateFormat).unix()
            const toDate = moment(cleanedData[cleanedData.length - 1].exposureOn, dateFormat).unix()
            return {
                fromDate,
                toDate,
            }
        }
        return {
            fromDate: 0,
            toDate: 0,
        }
    }, [cleanedData]);

    const formattedRange = React.useMemo(() => {
        return {
            fromDate: moment.unix(dateRange.fromDate).format(dateFormat),
            toDate: moment.unix(dateRange.toDate).format(dateFormat),
        };
    }, [dateRange]);


    const [measurements, setMeasurements] = React.useState<Measurements>({avg: 0, min: 0, max: 0});

    React.useLayoutEffect(() => {
        if (cleanedData && cleanedData.length > 0) {
            const pressures = cleanedData.map(x => x.concentration)
                .filter((x): x is number => x !== null && x !== 0);

            if (pressures.length === 0) {
                setMeasurements({
                    avg: 0,
                    min: 0,
                    max: 0,
                });
            } else {
                const acc = pressures.reduce((acc, curr) =>
                    acc + curr, 0);
                setMeasurements({
                    avg: (acc / pressures.length),
                    min: Math.min.apply(null, pressures),
                    max: Math.max.apply(null, pressures),
                });
            }
        }
    }, [cleanedData]);

    //added a filter to remove NSPM HRSD Collocated
    const locations = locationsData?.data?.filter(x => x.id !== 3) ?? [];

    const cardDateRange = React.useMemo(() => (
        loadingFilters ? 'Loading...' : `${
                moment.unix(dateRange.fromDate).format("YYYY/MM/DD")
              } - ${
                moment.unix(dateRange.toDate).format("YYYY/MM/DD")
              }`), [dateRange, loadingFilters]);

    //const currentLocation = React.useMemo(() => {
    //    return locations.find(x => x.id === currentLocationId);
    //}, [currentLocationId, locations]);
    
    const currentLocation = locations.find(x => x.id === currentLocationId);

    return (
        <AQMContext.Provider value={{
            currentLocation,
            currentRange: dateRange,
            data: cleanedData,
            measurements,
            loading: {
                filters: loadingFilters,
                locations: loadingLocations,
            },
            locations,
            fetchReport(locationId, middleDate) {
                setTargetDate({
                    unix: middleDate,
                    formatted: moment.unix(middleDate).format(dateFormat),
                });
                setCurrentLocationId(locationId);
            },
        }}>
            <Grid lsize={"400px"} rsize={"1fr"}>
                <ReportDownload/>
                <Card padding={0}
                      details={`Date Range : ${cardDateRange}`}>
                    <ConcentrationPlot/>
                </Card>
            </Grid>
            <Grid inverted lsize={"auto"}>
                <MapCard location = {currentLocation}/>
                <DataListCard/>
            </Grid>
        </AQMContext.Provider>
    );
};
